import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Banknote,
  Plus,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Copy,
  CheckCheck,
  Mail,
  RefreshCw,
  Trash2,
  Download,
  KeyRound,
  FileSignature,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending_user_id: {
    label: "Wartet auf User-ID",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    icon: Mail,
  },
  ready: {
    label: "Bereit",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle2,
  },
  error: {
    label: "Fehler",
    badge: "bg-rose-50 text-rose-700 border-rose-200",
    icon: AlertTriangle,
  },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-CH", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

export function SettingsPostFinanceSection({ companyId, isOwner }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newIban, setNewIban] = useState("");
  const [newEnv, setNewEnv] = useState("test");
  const [creating, setCreating] = useState(false);
  const [createdConn, setCreatedConn] = useState(null); // shows pubkey + email-template after create
  const [editing, setEditing] = useState(null); // {id, mftpf_user_id}
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({}); // {connId: bool}
  const [syncing, setSyncing] = useState({});
  const [syncingP002, setSyncingP002] = useState({});
  const [syncingCamt054, setSyncingCamt054] = useState({});
  const [syncingP002Chdd, setSyncingP002Chdd] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [copyHit, setCopyHit] = useState(null);

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/postfinance/connections`);
      setConnections(r.data || []);
    } catch (e) {
      toast.error("Konnte Verbindungen nicht laden.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/postfinance/connections`,
        { name: newName.trim(), iban: newIban || null, environment: newEnv },
      );
      setCreatedConn(r.data);
      setNewName("");
      setNewIban("");
      setShowCreate(false);
      await reload();
      toast.success("Connection angelegt + Key-Pair generiert.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen.");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveUserId = async (conn, value) => {
    setSaving(true);
    try {
      await apiClient.patch(
        `/companies/${companyId}/postfinance/connections/${conn.id}`,
        { mftpf_user_id: value },
      );
      setEditing(null);
      toast.success("MFTPF-User-ID gespeichert.");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (conn) => {
    setTesting((p) => ({ ...p, [conn.id]: true }));
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/postfinance/connections/${conn.id}/test`,
      );
      if (r.data.ok) {
        toast.success("Verbindung OK – PostFinance akzeptiert den Public-Key.");
      } else {
        toast.error(r.data.message || "Verbindung fehlgeschlagen.");
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen.");
    } finally {
      setTesting((p) => ({ ...p, [conn.id]: false }));
    }
  };

  const handleSync = async (conn) => {
    setSyncing((p) => ({ ...p, [conn.id]: true }));
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/postfinance/connections/${conn.id}/sync-camt`,
      );
      const { files_imported, files_skipped, errors } = r.data;
      if (errors?.length) {
        toast.warning(`${files_imported} importiert, ${files_skipped} übersprungen, ${errors.length} Fehler.`);
      } else if (files_imported > 0) {
        toast.success(`${files_imported} neue camt-Dateien geladen (${files_skipped} bereits bekannt).`);
      } else {
        toast.info(`Keine neuen Dateien (${files_skipped} bereits bekannt).`);
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync fehlgeschlagen.");
    } finally {
      setSyncing((p) => ({ ...p, [conn.id]: false }));
    }
  };

  const handleSyncP002Chdd = async (conn) => {
    setSyncingP002Chdd((p) => ({ ...p, [conn.id]: true }));
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/postfinance/connections/${conn.id}/sync-pain002-chdd`,
      );
      const { files_processed, invoices_marked_paid, journal_entries_created, rejected_count, errors } = r.data;
      if (errors?.length) {
        toast.warning(
          `${files_processed} LSV-Statusdateien · ${invoices_marked_paid} bezahlt · ${rejected_count} abgelehnt · ${errors.length} Fehler.`
        );
      } else if (invoices_marked_paid > 0 || rejected_count > 0) {
        toast.success(
          `${invoices_marked_paid} LSV-Einzüge bestätigt (${journal_entries_created} Buchungen), ${rejected_count} abgelehnt.`
        );
      } else if (files_processed > 0) {
        toast.info(`${files_processed} LSV-Statusdateien gelesen.`);
      } else {
        toast.info("Keine neuen LSV-Statusdateien.");
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "LSV-Status-Sync fehlgeschlagen.");
    } finally {
      setSyncingP002Chdd((p) => ({ ...p, [conn.id]: false }));
    }
  };

  const handleSyncCamt054 = async (conn) => {
    setSyncingCamt054((p) => ({ ...p, [conn.id]: true }));
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/postfinance/connections/${conn.id}/sync-camt054`,
      );
      const { files_processed, invoices_marked_paid, journal_entries_created, unmatched_count, errors } = r.data;
      if (errors?.length) {
        toast.warning(
          `${files_processed} Dateien · ${invoices_marked_paid} Rechnungen bezahlt · ${unmatched_count} unmatched · ${errors.length} Fehler.`
        );
      } else if (invoices_marked_paid > 0) {
        toast.success(
          `${invoices_marked_paid} Eingangs-Zahlungen automatisch gebucht (${journal_entries_created} Journal-Einträge), ${unmatched_count} ohne Zuordnung.`
        );
      } else if (files_processed > 0) {
        toast.info(`${files_processed} camt.054-Dateien gelesen — keine neuen Zahlungen zu buchen.`);
      } else {
        toast.info("Keine neuen camt.054-Dateien.");
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "camt.054-Sync fehlgeschlagen.");
    } finally {
      setSyncingCamt054((p) => ({ ...p, [conn.id]: false }));
    }
  };

  const handleSyncP002 = async (conn) => {
    setSyncingP002((p) => ({ ...p, [conn.id]: true }));
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/postfinance/connections/${conn.id}/sync-pain002`,
      );
      const { files_processed, bills_marked_paid, journal_entries_created, rejected_count, errors } = r.data;
      if (errors?.length) {
        toast.warning(
          `${files_processed} Status-Dateien verarbeitet, ${bills_marked_paid} bezahlt, ${rejected_count} abgelehnt — ${errors.length} Fehler.`
        );
      } else if (bills_marked_paid > 0 || rejected_count > 0) {
        toast.success(
          `${bills_marked_paid} Rechnungen als bezahlt gebucht (${journal_entries_created} Buchungen), ${rejected_count} abgelehnt.`
        );
      } else if (files_processed > 0) {
        toast.info(`${files_processed} Status-Dateien verarbeitet — keine Aktionen erforderlich.`);
      } else {
        toast.info("Keine neuen pain.002-Dateien.");
      }
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "pain.002-Sync fehlgeschlagen.");
    } finally {
      setSyncingP002((p) => ({ ...p, [conn.id]: false }));
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiClient.delete(
        `/companies/${companyId}/postfinance/connections/${confirmDelete.id}`,
      );
      toast.success("Verbindung gelöscht.");
      setConfirmDelete(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyHit(key);
      setTimeout(() => setCopyHit(null), 1500);
    } catch {
      // ignore
    }
  };

  const downloadPublicKey = (conn) => {
    const blob = new Blob([conn.public_key_openssh + "\n"], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `mftpf_pubkey_${conn.name.replace(/[^a-z0-9]/gi, "_")}.pub`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const buildEmailTemplate = (conn, companyName = "Ihre Firma") => {
    return `An: tscorp@postfinance.ch
Betreff: Public-Key für MFTPF-Aktivierung – ${companyName}

Sehr geehrte Damen und Herren

Bitte hinterlegen Sie den nachstehenden Public-Key (RSA-4096) für unseren MFTPF-Zugang.

Firma:        ${companyName}
Bezeichnung:  ${conn.name}
${conn.iban ? `IBAN:         ${conn.iban}` : ""}
Umgebung:     ${conn.environment === "test" ? "Test (isotest.postfinance.ch)" : "Production (mftp1.postfinance.ch)"}
Fingerprint:  ${conn.fingerprint_sha256}

Public-Key (OpenSSH, RSA-4096):
${conn.public_key_openssh}

Bitte teilen Sie uns die zugehörige MFTPF-User-ID nach Aktivierung mit.

Freundliche Grüsse`;
  };

  return (
    <Card
      id="section-postfinance"
      className="p-4 border border-slate-200 bg-white shadow-sm scroll-mt-20"
      data-testid="settings-postfinance-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Banknote className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-slate-900">PostFinance MFTPF</h2>
            <p className="text-xs text-slate-500">
              Automatischer Datenaustausch via SFTP. Pro Konto eine Verbindung anlegen.
              Schritt 1: Verbindung anlegen → Public-Key wird generiert.
              Schritt 2: Public-Key per E-Mail an <code className="font-mono text-[11px]">tscorp@postfinance.ch</code>.
              Schritt 3: User-ID eintragen → Verbindung testen → camt-Files automatisch laden.
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          className="rounded-full bg-slate-900 text-slate-50"
          onClick={() => setShowCreate((s) => !s)}
          disabled={!isOwner}
          data-testid="postfinance-add-connection-button"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Verbindung
        </Button>
      </div>

      {/* Voraussetzungen & Anleitung */}
      <details className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="postfinance-prerequisites">
        <summary className="cursor-pointer text-xs font-semibold text-sky-900 select-none">
          📋 Was Sie von PostFinance benötigen (vor der ersten Verbindung)
        </summary>
        <div className="mt-3 text-[12px] text-slate-700 space-y-3">
          <div>
            <div className="font-semibold text-slate-900 mb-1">1. PostFinance MFTPF-Vertrag aktivieren</div>
            <ul className="list-disc ml-5 space-y-1">
              <li>Anruf bei Ihrer PostFinance-Kundenberatung oder via E-Finance → Anfrage <span className="font-mono">"MFTPF (Managed File Transfer PostFinance) für Geschäftskunden"</span>.</li>
              <li>PostFinance prüft den Vertrag, schaltet die Funktion frei und sendet Ihnen die <span className="font-semibold">Vertragsnummer</span> zu.</li>
              <li>Kosten: Einrichtungsgebühr + monatliche Gebühr (je nach Volumen).</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-1">2. Gewünschte Services bei PostFinance bestellen</div>
            <ul className="list-disc ml-5 space-y-1">
              <li><span className="font-semibold">camt.053</span> – Tagesauszug (Eingänge/Ausgänge) → automatischer Bankabgleich.</li>
              <li><span className="font-semibold">camt.054</span> – Detail-Avis mit QR-Referenz → automatische Verbuchung von QR-Rechnungs-Eingängen.</li>
              <li><span className="font-semibold">pain.001</span> – Zahlungsaufträge (Vergütungen) → Bulk-Zahlungsläufe für Lieferantenrechnungen.</li>
              <li><span className="font-semibold">pain.002</span> – Statusrückmeldungen → automatisches Verbuchen erfolgreicher Zahlungen.</li>
              <li><span className="font-semibold">pain.008 (LSV+ / CH-DD)</span> – Lastschrift-Verfahren → Direkt-Belastung bei Kundenkonten (Mandate erforderlich).</li>
              <li><span className="font-semibold">pain.002-chdd</span> – Statusrückmeldung LSV → automatische Verbuchung erfolgreich eingezogener Rechnungen.</li>
            </ul>
            <div className="text-[11px] text-slate-500 mt-1">
              Tipp: Sie müssen nicht alle Services aktivieren. Empfehlung für KMU: <span className="font-semibold">camt.053 + camt.054 + pain.001 + pain.002</span> — deckt 95 % der Buchhaltung ab.
            </div>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-1">3. SFTP-Zugang anfordern</div>
            <ul className="list-disc ml-5 space-y-1">
              <li>Nach Vertragsabschluss erhalten Sie eine E-Mail von <span className="font-mono">tscorp@postfinance.ch</span> mit Anleitung zur Public-Key-Hinterlegung.</li>
              <li>Hier in der Software: Verbindung anlegen → Public-Key wird generiert → Sie senden ihn an PostFinance.</li>
              <li>PostFinance hinterlegt den Key, schaltet das SFTP-Postfach frei und sendet Ihnen die <span className="font-semibold">MFTPF-User-ID</span> zurück (typisch: <span className="font-mono">USER_xxxxx</span>).</li>
              <li>User-ID hier eintragen → "Testen" → fertig. Ab jetzt läuft alles automatisch im Hintergrund.</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-1">4. Andere Banken (UBS, ZKB, Raiffeisen, Credit Suisse)</div>
            <p>
              Diese Software ist aktuell nur für <span className="font-semibold">PostFinance MFTPF</span> implementiert.
              Bei anderen Banken können Sie:
            </p>
            <ul className="list-disc ml-5 space-y-1 mt-1">
              <li>camt.053-Files manuell exportieren (E-Banking) und unter <span className="font-mono">Bank-Abgleich → Import</span> hochladen.</li>
              <li>QR-Rechnungen werden trotzdem korrekt erstellt — Ihre Kunden können bei jeder Schweizer Bank bezahlen.</li>
              <li>Wir können bei Bedarf weitere Banken (z.B. UBS EBICS, Raiffeisen) auf Anfrage integrieren.</li>
            </ul>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
            <span className="font-semibold">Sicherheitshinweis:</span> Der private Schlüssel verlässt diesen Server nie.
            Er wird AES-verschlüsselt in der Datenbank abgelegt (Master-Key in <code className="font-mono">POSTFINANCE_MASTER_KEY</code> env-Var).
            Sie sehen nur den Public-Key — den dürfen Sie unbedenklich teilen.
          </div>
        </div>
      </details>

      {showCreate && (
        <form
          className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200"
          onSubmit={handleCreate}
          data-testid="postfinance-create-form"
        >
          <div className="md:col-span-4 space-y-1">
            <Label htmlFor="pf-name">Bezeichnung</Label>
            <Input
              id="pf-name" value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="z.B. Hauptkonto"
              data-testid="postfinance-name-input"
              required
            />
          </div>
          <div className="md:col-span-4 space-y-1">
            <Label htmlFor="pf-iban">IBAN (optional)</Label>
            <Input
              id="pf-iban" value={newIban}
              onChange={(e) => setNewIban(e.target.value)}
              placeholder="CH93 0076 2011 6238 5295 7"
              data-testid="postfinance-iban-input"
            />
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label htmlFor="pf-env">Umgebung</Label>
            <Select value={newEnv} onValueChange={setNewEnv}>
              <SelectTrigger id="pf-env" data-testid="postfinance-env-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="production">Production</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button
              type="submit" size="sm"
              className="w-full rounded-full bg-slate-900 text-slate-50"
              disabled={creating}
              data-testid="postfinance-create-submit"
            >
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                <><KeyRound className="h-3.5 w-3.5 mr-1" /> Anlegen</>
              )}
            </Button>
          </div>
        </form>
      )}

      {createdConn && (
        <div className="mb-4 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4 space-y-3" data-testid="postfinance-onboarding-banner">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
            <h3 className="text-sm font-semibold text-emerald-900">
              Verbindung "{createdConn.name}" angelegt – nächster Schritt:
            </h3>
          </div>
          <p className="text-xs text-emerald-900">
            Senden Sie diesen Public-Key per E-Mail an <code className="font-mono">tscorp@postfinance.ch</code>.
            PostFinance bestätigt mit der MFTPF-User-ID, die Sie hier dann eintragen.
          </p>
          <div className="space-y-2">
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button" size="sm" variant="outline"
                onClick={() => copyToClipboard(createdConn.public_key_openssh, "pubkey")}
                data-testid="postfinance-copy-pubkey-button"
              >
                {copyHit === "pubkey" ? <CheckCheck className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                Public-Key kopieren
              </Button>
              <Button
                type="button" size="sm" variant="outline"
                onClick={() => downloadPublicKey(createdConn)}
                data-testid="postfinance-download-pubkey-button"
              >
                <Download className="h-3.5 w-3.5 mr-1" /> .pub-Datei herunterladen
              </Button>
              <Button
                type="button" size="sm" variant="outline"
                onClick={() => copyToClipboard(buildEmailTemplate(createdConn), "email")}
                data-testid="postfinance-copy-email-template"
              >
                {copyHit === "email" ? <CheckCheck className="h-3.5 w-3.5 mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                E-Mail-Vorlage kopieren
              </Button>
              <Button
                type="button" size="sm" variant="ghost"
                onClick={() => setCreatedConn(null)}
              >
                Schliessen
              </Button>
            </div>
            <pre className="text-[10px] font-mono bg-white border border-emerald-200 rounded p-2 overflow-x-auto max-h-24" data-testid="postfinance-pubkey-pre">
{createdConn.public_key_openssh}
            </pre>
            <p className="text-[10px] text-emerald-800">
              Fingerprint: <code className="font-mono">{createdConn.fingerprint_sha256}</code>
            </p>
          </div>
        </div>
      )}

      {loading && connections.length === 0 && (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Lade Verbindungen …
        </div>
      )}

      {connections.length === 0 && !loading && (
        <div className="text-center py-6 text-sm text-slate-400" data-testid="postfinance-empty">
          Noch keine PostFinance-Verbindung. Klicken Sie auf "Verbindung", um zu starten.
        </div>
      )}

      <div className="space-y-3" data-testid="postfinance-connections-list">
        {connections.map((c) => {
          const meta = STATUS_META[c.status] || STATUS_META.error;
          const Icon = meta.icon;
          const isReady = c.status === "ready";
          return (
            <Card key={c.id} className="p-3 border border-slate-200" data-testid={`postfinance-connection-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">{c.name}</span>
                    <Badge variant="outline" className={`text-[10px] h-5 ${meta.badge}`}>
                      <Icon className="h-3 w-3 mr-0.5" /> {meta.label}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] h-5 bg-slate-50 text-slate-600 capitalize">
                      {c.environment}
                    </Badge>
                    {c.iban && (
                      <span className="text-[11px] font-mono text-slate-500">{c.iban}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 space-y-0.5">
                    <div>
                      Host: <code className="font-mono">{c.host}:{c.port}</code>
                      {" · "}
                      MFTPF-User-ID:{" "}
                      {editing?.id === c.id ? (
                        <span className="inline-flex gap-1 items-center">
                          <Input
                            type="text"
                            className="h-6 w-40 text-[11px] inline-flex"
                            value={editing.mftpf_user_id}
                            onChange={(e) => setEditing({ ...editing, mftpf_user_id: e.target.value })}
                            data-testid={`postfinance-userid-input-${c.id}`}
                          />
                          <Button
                            type="button" size="sm" variant="outline"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => handleSaveUserId(c, editing.mftpf_user_id)}
                            disabled={saving}
                            data-testid={`postfinance-userid-save-${c.id}`}
                          >Speichern</Button>
                          <Button
                            type="button" size="sm" variant="ghost"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => setEditing(null)}
                          >X</Button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="font-mono underline hover:text-slate-900"
                          onClick={() => setEditing({ id: c.id, mftpf_user_id: c.mftpf_user_id || "" })}
                          data-testid={`postfinance-userid-edit-${c.id}`}
                        >
                          {c.mftpf_user_id || "— noch eintragen"}
                        </button>
                      )}
                    </div>
                    {c.last_test_at && (
                      <div>
                        Letzter Test:{" "}
                        <span className={c.last_test_ok ? "text-emerald-700" : "text-rose-700"}>
                          {fmtDateTime(c.last_test_at)} — {c.last_test_message}
                        </span>
                      </div>
                    )}
                    {c.last_camt_sync_at && (
                      <div>Letzter camt-Sync: {fmtDateTime(c.last_camt_sync_at)}</div>
                    )}
                    {c.last_pain002_sync_at && (
                      <div data-testid={`postfinance-last-pain002-${c.id}`}>
                        Letzter Status-Sync (pain.002): {fmtDateTime(c.last_pain002_sync_at)}
                      </div>
                    )}
                    {c.last_camt054_sync_at && (
                      <div data-testid={`postfinance-last-camt054-${c.id}`}>
                        Letzter Eingangs-Sync (camt.054): {fmtDateTime(c.last_camt054_sync_at)}
                      </div>
                    )}
                    {c.last_pain002_chdd_sync_at && (
                      <div data-testid={`postfinance-last-pain002-chdd-${c.id}`}>
                        Letzter LSV-Status-Sync (pain.002-chdd): {fmtDateTime(c.last_pain002_chdd_sync_at)}
                      </div>
                    )}
                    {(c.last_camt_sync_error || c.last_camt054_sync_error || c.last_pain002_sync_error || c.last_pain002_chdd_sync_error) && (
                      <div
                        className="mt-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-[10.5px] text-rose-900"
                        data-testid={`postfinance-sync-error-banner-${c.id}`}
                      >
                        <div className="font-semibold mb-0.5 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> Letzte Sync-Fehler
                        </div>
                        <ul className="space-y-0.5">
                          {c.last_camt_sync_error && (
                            <li><span className="font-medium">camt.053/.054:</span> {c.last_camt_sync_error}</li>
                          )}
                          {c.last_camt054_sync_error && (
                            <li><span className="font-medium">Eingänge:</span> {c.last_camt054_sync_error}</li>
                          )}
                          {c.last_pain002_sync_error && (
                            <li><span className="font-medium">Status pain.001:</span> {c.last_pain002_sync_error}</li>
                          )}
                          {c.last_pain002_chdd_sync_error && (
                            <li><span className="font-medium">LSV-Status:</span> {c.last_pain002_chdd_sync_error}</li>
                          )}
                        </ul>
                      </div>
                    )}
                    <div className="font-mono text-[10px] text-slate-400">
                      Fingerprint: {c.fingerprint_sha256}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <Button
                    type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => copyToClipboard(c.public_key_openssh, `pk-${c.id}`)}
                    data-testid={`postfinance-copy-pubkey-${c.id}`}
                  >
                    {copyHit === `pk-${c.id}` ? <CheckCheck className="h-3 w-3 mr-1" /> : <KeyRound className="h-3 w-3 mr-1" />}
                    Public-Key
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => handleTest(c)}
                    disabled={testing[c.id] || !c.mftpf_user_id}
                    data-testid={`postfinance-test-button-${c.id}`}
                  >
                    {testing[c.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                    Testen
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => handleSync(c)}
                    disabled={syncing[c.id] || !isReady}
                    data-testid={`postfinance-sync-button-${c.id}`}
                  >
                    {syncing[c.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                    camt laden
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => handleSyncP002(c)}
                    disabled={syncingP002[c.id] || !isReady}
                    data-testid={`postfinance-sync-pain002-button-${c.id}`}
                    title="Holt pain.002 Status-Dateien und bucht bezahlte Rechnungen automatisch ab."
                  >
                    {syncingP002[c.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCheck className="h-3 w-3 mr-1" />}
                    Status abrufen
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => handleSyncCamt054(c)}
                    disabled={syncingCamt054[c.id] || !isReady}
                    data-testid={`postfinance-sync-camt054-button-${c.id}`}
                    title="Holt camt.054 Eingangsavise und matched offene Kundenrechnungen automatisch über QR-Referenz."
                  >
                    {syncingCamt054[c.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Banknote className="h-3 w-3 mr-1" />}
                    Eingänge abrufen
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => handleSyncP002Chdd(c)}
                    disabled={syncingP002Chdd[c.id] || !isReady}
                    data-testid={`postfinance-sync-pain002-chdd-button-${c.id}`}
                    title="Liest pain.002-Status für LSV-Lastschrift-Einzüge und bucht erfolgreiche automatisch ab."
                  >
                    {syncingP002Chdd[c.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FileSignature className="h-3 w-3 mr-1" />}
                    LSV-Status abrufen
                  </Button>
                  <Button
                    type="button" size="sm" variant="ghost"
                    className="h-7 text-[11px] text-rose-600 hover:bg-rose-50"
                    onClick={() => setConfirmDelete(c)}
                    data-testid={`postfinance-delete-button-${c.id}`}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Löschen
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="postfinance-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>PostFinance-Verbindung löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Verbindung <strong>{confirmDelete?.name}</strong> wird gelöscht.
              Bereits heruntergeladene camt-Files bleiben erhalten. Der Public-Key wird
              ungültig — Sie müssten PostFinance den neuen Key später nachreichen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={handleDelete}
              data-testid="postfinance-delete-confirm"
            >
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
